import { useMemo } from 'react';

import { type SelectOption } from '@/components/forms/SelectField';
import { featuredBrands } from '@/config/brands';
import { bodyChoiceLabels } from '@/schemas/bodyChoices';
import { BUDGET_STEPS_USD, MIN_YEAR, maxYear } from '@/schemas/vehicleSearch';
import { countryName, fuelLabels, transmissionLabels } from '@/utils/vehicleFormat';

import { useVehicleFacets } from './useVehicles';

const toOptions = (labels: Record<string, string>): SelectOption[] =>
  Object.entries(labels).map(([value, label]) => ({ value, label }));

const fuelOptions = toOptions(fuelLabels);
const transmissionOptions = toOptions(transmissionLabels);
/**
 * Body types are offered in full rather than from the facets, unlike the
 * countries below. A category with nothing in it is still the right question
 * to be asked — "trucks & commercials" returning nothing today is an honest
 * answer that sends the visitor to the import form, whereas hiding the option
 * tells them this site does not do trucks at all.
 */
const bodyOptions = toOptions(bodyChoiceLabels);
/**
 * The markets named in the brief, always offered even before stock exists.
 * China joined them on 2026-10-18.
 */
const baseCountries = ['JP', 'GB', 'CN'];

export type VehicleSearchOptions = {
  makes: SelectOption[];
  /** Only the chosen make's models; empty until a make is picked. */
  models: SelectOption[];
  years: SelectOption[];
  budgets: SelectOption[];
  fuels: SelectOption[];
  transmissions: SelectOption[];
  countries: SelectOption[];
  /** Body-type categories, which are not one-to-one with `BodyType`. */
  bodies: SelectOption[];
};

/**
 * Every choice the vehicle search offers, merged from the featured brands and
 * whatever is actually in stock. Kept out of the form so the form is layout
 * only, and so /cars and the home hero cannot drift apart.
 */
export function useVehicleSearchOptions(make: string): VehicleSearchOptions {
  const { data: facets } = useVehicleFacets();

  const makes = useMemo<SelectOption[]>(() => {
    // `string` on both sides, not the Brand name union: the facets below
    // carry whatever makes are actually in stock, which is a superset of the
    // featured list and cannot be narrowed to it.
    const names = new Map<string, string>(featuredBrands.map((b) => [b.slug, b.name]));
    facets?.makes.forEach((m) => names.set(m.slug, m.name));
    return [...names]
      .map(([value, label]) => ({ value, label }))
      .sort((a, b) => a.label.localeCompare(b.label));
  }, [facets]);

  const models = useMemo<SelectOption[]>(
    () =>
      facets?.makes
        .find((m) => m.slug === make)
        ?.models.map((m) => ({ value: m.slug, label: m.name })) ?? [],
    [facets, make],
  );

  const countries = useMemo<SelectOption[]>(
    () =>
      [...new Set([...baseCountries, ...(facets?.countries ?? [])])]
        .map((code) => ({ value: code, label: countryName(code) }))
        .sort((a, b) => a.label.localeCompare(b.label)),
    [facets],
  );

  const budgets = useMemo<SelectOption[]>(
    () =>
      BUDGET_STEPS_USD.map((amount) => ({
        value: String(amount),
        label: `Up to USD ${amount.toLocaleString('en-US')}`,
      })),
    [],
  );

  const years = useMemo<SelectOption[]>(() => {
    const list: SelectOption[] = [];
    for (let y = maxYear(); y >= MIN_YEAR; y -= 1)
      list.push({ value: String(y), label: String(y) });
    return list;
  }, []);

  return {
    makes,
    models,
    years,
    budgets,
    fuels: fuelOptions,
    transmissions: transmissionOptions,
    countries,
    bodies: bodyOptions,
  };
}
